<template>
  <Modal
    class="multiple-events-modal"
    :is-displayed="isDisplayed"
    v-bind="$attrs"
    :modal-shortcuts="modalShortcuts"
    @close="close"
  >
    <template #header>
      {{ header }}
    </template>
    <div class="block">
      <div class="line">
        <input
          id="this"
          v-model="choiceIndex"
          type="radio"
          name="choice"
          value="0"
          :disabled="!availableChoices.this"
        />
        <label for="this" :class="{ isDisabled: !availableChoices.this }"
          >This event</label
        >
      </div>
      <div class="line">
        <input
          id="following"
          v-model="choiceIndex"
          type="radio"
          name="choice"
          value="1"
          :disabled="!availableChoices.following"
        />
        <label
          for="following"
          :class="{ isDisabled: !availableChoices.following }"
          >This and following events</label
        >
      </div>
      <div class="line">
        <input
          id="all"
          v-model="choiceIndex"
          type="radio"
          name="choice"
          value="2"
          :disabled="!availableChoices.all"
        />
        <label for="all" :class="{ isDisabled: !availableChoices.all }"
          >All events</label
        >
      </div>
    </div>
    <div class="buttons">
      <BaseButton
        kind="main"
        type="tertiary"
        text="Cancel"
        :tooltip="{ shortcut: modalActions.closeModal.shortcutsDisplay }"
        tooltip-display="always"
        @click="close()"
      />
      <BaseButton
        kind="main"
        type="primary"
        :text="actionName"
        :tooltip="{ shortcut: modalActions.validateModal.shortcutsDisplay }"
        tooltip-display="always"
        @click="save()"
      />
    </div>
  </Modal>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref, watch } from "vue";

import { modalActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import * as inject from "@/components/inject";
import type {
  MultipleEventModalContent,
  MultipleEventsChoices,
  SafeUpdate,
  SimpleModalContent,
} from "@/components/modal";
import {
  defaultMultipleEventsAvailableChoice,
  MultipleEventsAvailableChoices,
} from "@/components/modal";
import { ShortcutList, validateForm } from "@/components/shortcuts";
import { useOverlayPresence } from "@/composables/useOverlayPresence";

import Modal from "./Modal.vue";

export default defineComponent({
  components: {
    BaseButton,
    Modal,
  },
  setup() {
    const header = ref("");
    const isDisplayed = ref(false);
    const releaseOverlayPresence = useOverlayPresence(isDisplayed);
    onBeforeUnmount(releaseOverlayPresence);
    const choices: MultipleEventsChoices[] = ["this", "following", "all"];
    const choiceIndex = ref<number>(0);
    const choice = computed<MultipleEventsChoices>(
      () => choices[choiceIndex.value]
    );
    const availableChoices = ref<MultipleEventsAvailableChoices>(
      defaultMultipleEventsAvailableChoice
    );
    const resetChoice = () => (choiceIndex.value = 0);
    watch(isDisplayed, (v) => {
      if (!v) resetChoice();
    });
    const saveAction = ref<SafeUpdate>();
    const actionName = ref("Save");
    const onClose = ref<(() => void) | null>(null);
    const simpleModal = inject.simpleModal();
    const meetingModalContent = ref<SimpleModalContent | null | undefined>(
      null
    );
    const showModal = (
      multipleEventModalContent: MultipleEventModalContent
    ) => {
      header.value = multipleEventModalContent.header;
      availableChoices.value = multipleEventModalContent.choices;
      saveAction.value = multipleEventModalContent.action.func;
      meetingModalContent.value = multipleEventModalContent.meetingModalContent;
      if (multipleEventModalContent.action.name)
        actionName.value = multipleEventModalContent.action.name;
      onClose.value = multipleEventModalContent.onCloseFunc
        ? multipleEventModalContent.onCloseFunc
        : null;
      isDisplayed.value = true;
    };
    const close = () => {
      if (onClose.value) onClose.value();
      isDisplayed.value = false;
    };
    const save = () => {
      const selectedChoice = choice.value; // Required because resetChoice is called after closing the modal
      if (meetingModalContent.value) {
        simpleModal.value.showModal({
          ...meetingModalContent.value,
          actions: [
            {
              name: "Notify",
              type: "primary",
              func: () =>
                saveAction.value
                  ? saveAction.value(true, selectedChoice)
                  : null,
            },
            {
              name: "Don't notify",
              type: "secondary",
              func: () =>
                saveAction.value
                  ? saveAction.value(false, selectedChoice)
                  : null,
            },
          ],
        });
      } else {
        if (saveAction.value) saveAction.value(false, selectedChoice);
      }
      isDisplayed.value = false;
    };
    const previousChoice = () => {
      if (choiceIndex.value > 0) choiceIndex.value--;
      else choiceIndex.value = choices.length - 1;
    };
    const nextChoice = () => {
      if (choiceIndex.value < choices.length - 1) choiceIndex.value++;
      else resetChoice();
    };
    const modalShortcuts = computed<ShortcutList>(() => [
      { keys: validateForm, action: save },
      { keys: ref("up"), action: previousChoice },
      { keys: ref("down"), action: nextChoice },
      { keys: ref("tab"), action: nextChoice },
    ]);
    return {
      actionName,
      availableChoices,
      choice,
      choiceIndex,
      close,
      header,
      isDisplayed,
      modalActions,
      modalShortcuts,
      save,
      showModal,
    };
  },
});
</script>

<style lang="scss" scoped>
.multiple-events-modal {
  @extend %font-caption-regular;
  color: red;
  :deep(.modal .modal-wrapper) {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
  }
  .title {
    margin-inline-start: 4px;
  }

  .block {
    display: flex;
    flex-direction: column;
    row-gap: 10px;

    .line {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 8px;

      input[type="radio"] {
        filter: grayscale(1);
        margin: 0;
      }
      label.isDisabled {
        @include themed() {
          color: t($grey-20);
        }
      }
    }
  }

  .buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    column-gap: 8px;
  }
}
</style>
